/**
 * CRM Widget Auto Modal Embed Script
 * Automatically displays the CRM form in a modal on page load
 *
 * Usage:
 * <script src="https://your-domain.com/embeds/auto-modal.js"
 *         data-platform-account-guid="YOUR-GUID"
 *         data-market-guid="YOUR-MARKET-GUID"  // optional
 *         data-platform-key="YOUR-API-KEY"
 *         data-source="your-source"
 *         data-auto-show-delay="1000">
 * </script>
 */

(function () {
  "use strict";

  // Default widget URL - update this for production
  const DEFAULT_WIDGET_URL = "https://crm-widget.getonpropeller.com/";

  type ModalConfig = {
    platformAccountGuid: string;
    marketGuid?: string;
    platformKey: string;
    source?: string;
    lang?: string;
    autoShowDelay?: number;
    baseUrl?: string;
  };

  class CRMAutoModal {
    private config: ModalConfig;
    private popupId = "crm-widget-popup";
    private iframeId = "crm-widget-popup-iframe";
    private _onKeydown: ((e: KeyboardEvent) => void) | null = null;

    constructor(config: ModalConfig) {
      this.config = config;
    }

    private ensureFontLoaded(): void {
      if (document.getElementById("crm-widget-popup-font")) return;

      const link = document.createElement("link");
      link.id = "crm-widget-popup-font";
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap";
      document.head.appendChild(link);
    }

    private createStyles(): void {
      if (document.getElementById("crm-widget-popup-styles")) return;

      this.ensureFontLoaded();

      const style = document.createElement("style");
      style.id = "crm-widget-popup-styles";
      style.textContent = `
        .crm-widget-popup {
          position: fixed;
          top: auto;
          bottom: 40px;
          right: 250px;
          left: auto;
          transform: translateY(20px) scale(0.95);
          width: 420px;
          height: 550px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
          z-index: 999999;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          opacity: 0;
          transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          pointer-events: none;
          font-family: 'Poppins', -apple-system, 'Segoe UI', Roboto, sans-serif;
        }
        .crm-widget-popup.show {
          opacity: 1;
          transform: translateY(0) scale(1);
          pointer-events: auto;
        }
        .crm-widget-popup-header {
          position: relative;
          padding: 20px 24px;
          background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        .crm-widget-popup-title {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
          font-family: inherit;
          letter-spacing: -0.025em;
        }
        .crm-widget-popup-close {
          background: rgba(255, 255, 255, 0.15);
          border: none;
          color: white;
          font-size: 20px;
          cursor: pointer;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          line-height: 1;
          padding: 0;
          flex-shrink: 0;
        }
        .crm-widget-popup-close:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: scale(1.05);
        }
        .crm-widget-popup-iframe-container {
          flex: 1;
          position: relative;
          overflow: auto;
          background: #ffffff;
        }
        .crm-widget-popup-iframe {
          width: 100%;
          height: 100%;
          border: none;
          display: block;
        }
        @media (max-width: 768px) {
          .crm-widget-popup {
            top: auto;
            bottom: 0;
            left: 0;
            right: 0;
            transform: translateY(20px) scale(0.95);
            width: 100%;
            height: 75vh;
            border-radius: 16px 16px 0 0;
            max-height: 550px;
          }
          .crm-widget-popup.show {
            transform: translateY(0) scale(1);
          }
        }
        @media (max-width: 480px) {
          .crm-widget-popup {
            height: 85vh;
          }
        }
      `;
      document.head.appendChild(style);
    }

    private createModal(): void {
      // Prevent duplicate popups and listeners
      if (document.getElementById(this.popupId)) return;

      // Remove old listener if it exists
      if (this._onKeydown) {
        document.removeEventListener("keydown", this._onKeydown);
        this._onKeydown = null;
      }

      // Create popup
      const popup = document.createElement("div");
      popup.id = this.popupId;
      popup.className = "crm-widget-popup";
      popup.setAttribute("role", "dialog");
      popup.setAttribute("aria-modal", "false");
      popup.setAttribute("aria-labelledby", "crm-widget-popup-title");

      // Create header
      const header = document.createElement("div");
      header.className = "crm-widget-popup-header";

      const title = document.createElement("h2");
      title.id = "crm-widget-popup-title";
      title.className = "crm-widget-popup-title";
      title.textContent = "Need An Escape?";

      const closeBtn = document.createElement("button");
      closeBtn.className = "crm-widget-popup-close";
      closeBtn.setAttribute("aria-label", "Close popup");
      closeBtn.innerHTML = "&times;";
      closeBtn.onclick = () => this.close();

      header.appendChild(title);
      header.appendChild(closeBtn);

      // Create iframe container
      const iframeContainer = document.createElement("div");
      iframeContainer.className = "crm-widget-popup-iframe-container";

      const iframe = document.createElement("iframe");
      iframe.id = this.iframeId;
      iframe.className = "crm-widget-popup-iframe";
      iframe.title = "CRM Contact Form";
      iframe.src = this.buildIframeUrl();
      iframe.setAttribute("loading", "eager");
      window.addEventListener("message", (event) => {
        if (
          event.source !== iframe.contentWindow ||
          event.origin !== new URL(iframe.src).origin
        )
          return;
        if (event.data?.source !== "crm-widget-locale") return;
        title.textContent = event.data.embed.escape;
        closeBtn.setAttribute("aria-label", event.data.embed.close);
        iframe.title = event.data.embed.title;
      });

      iframeContainer.appendChild(iframe);

      // Assemble popup
      popup.appendChild(header);
      popup.appendChild(iframeContainer);

      // Close on ESC key
      this._onKeydown = (e: KeyboardEvent) => {
        if (e.key === "Escape" && popup.classList.contains("show")) {
          this.close();
        }
      };
      document.addEventListener("keydown", this._onKeydown);

      document.body.appendChild(popup);
    }

    private buildIframeUrl(): string {
      const rawBaseUrl = this.config.baseUrl || DEFAULT_WIDGET_URL;
      const normalizedBaseUrl = rawBaseUrl.replace(/\/+$/, "");
      const params = new URLSearchParams({
        platformAccountGuid: this.config.platformAccountGuid,
        platformKey: this.config.platformKey,
        popup: "true",
      });
      if (this.config.lang !== undefined) params.set("lang", this.config.lang);

      if (this.config.marketGuid) {
        params.set("marketGuid", this.config.marketGuid);
      }

      if (this.config.source) {
        params.set("source", this.config.source);
      }

      return `${normalizedBaseUrl}/?${params.toString()}`;
    }

    public show(): void {
      const popup = document.getElementById(this.popupId);
      if (popup) {
        // Trigger animation
        requestAnimationFrame(() => {
          popup.classList.add("show");
        });
      }
    }

    public close(): void {
      const popup = document.getElementById(this.popupId);
      if (popup) {
        popup.classList.remove("show");
      }

      // Remove keydown listener to prevent memory leak
      if (this._onKeydown) {
        document.removeEventListener("keydown", this._onKeydown);
        this._onKeydown = null;
      }
    }

    public init(): void {
      this.createStyles();
      this.createModal();

      const parsedDelay = Number(this.config.autoShowDelay);
      const delay = Number.isFinite(parsedDelay) ? parsedDelay : 1000;
      setTimeout(() => {
        this.show();
      }, delay);
    }
  }

  // Auto-initialize from script tag attributes
  function autoInit() {
    // Find the script tag that loaded this module
    const script = document.querySelector<HTMLScriptElement>(
      'script[src*="auto-modal.js"]',
    );
    if (!script) {
      console.error(
        '[CRM Widget] Could not find script tag with src containing "auto-modal.js"',
      );
      return;
    }

    const platformAccountGuid = script.getAttribute(
      "data-platform-account-guid",
    );
    const marketGuid = script.getAttribute("data-market-guid") || undefined;
    const platformKey = script.getAttribute("data-platform-key");

    if (!platformAccountGuid || !platformKey) {
      console.error(
        "[CRM Widget] Missing required attributes: data-platform-account-guid or data-platform-key",
      );
      return;
    }

    const config: ModalConfig = {
      platformAccountGuid,
      lang: script.getAttribute("data-lang") ?? undefined,
      marketGuid,
      platformKey,
      source: script.getAttribute("data-source") || undefined,
      autoShowDelay: parseInt(
        script.getAttribute("data-auto-show-delay") || "1000",
        10,
      ),
      baseUrl: script.getAttribute("data-base-url") || undefined,
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        const modal = new CRMAutoModal(config);
        modal.init();
      });
    } else {
      const modal = new CRMAutoModal(config);
      modal.init();
    }
  }

  autoInit();
})();
